import { useEffect } from "react";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { PromoFeatureFlag, PromoFeatureName, PromoType } from "./types";
import { useAuthContext } from "providers/Auth";
import {
  shouldApplyPromo,
  cleanupPromoStorage,
  cleanupChatInboxPromoStorage,
  chatInboxPromoStorageKey
} from "./utils";
import { useSessionStorage } from "shared/hooks/use-session-storage";
import { useLocalStorage } from "shared/hooks/use-local-storage";
import moment from "moment";
import { CHAT_INBOX_EXPIRY_DATE, FEATURE_FLAG_EXPIRY_DATE } from "./constants";

export const useShowPromo = () => {
  const showPromoBannerFF = useFeatureFlagEnabled(PromoFeatureFlag.Banner);
  const showPromoModalFF = useFeatureFlagEnabled(PromoFeatureFlag.Modal);
  const promoModalSessionData = useSessionStorage("hidePromoModal");
  const promoBannerSessionData = useSessionStorage("hidePromoBanner");

  const { type, auth } = useAuthContext();

  useEffect(() => {
    if (moment().isAfter(FEATURE_FLAG_EXPIRY_DATE))
      cleanupPromoStorage(PromoFeatureName.CombinedIncome);
  }, []);

  const showPromoModal = shouldApplyPromo(
    PromoFeatureName.CombinedIncome,
    PromoType.Modal,
    {
      isBoomScreen: type.isBoomScreen,
      partnerCreatedAt: auth?.created_at,
      featureFlagEnabled: showPromoModalFF,
      sessionPromoData: promoModalSessionData
    }
  );

  const showPromoBanner =
    !showPromoModal &&
    shouldApplyPromo(PromoFeatureName.CombinedIncome, PromoType.Banner, {
      isBoomScreen: type.isBoomScreen,
      partnerCreatedAt: auth?.created_at,
      featureFlagEnabled: showPromoBannerFF,
      sessionPromoData: promoBannerSessionData
    });

  return {
    showPromoModal,
    showPromoBanner
  };
};

// No feature-flag gate here, unlike the older promos: the audience is already
// pinned to the partners whose Chat actually moved (native_chat), which ops flip
// as they migrate, so a second switch would only add a silent-failure mode.
export const useShowChatInboxPromo = () => {
  const modalSeen = useLocalStorage(chatInboxPromoStorageKey("modalSeen"));
  const dismissed = useLocalStorage(chatInboxPromoStorageKey("dismissed"));

  const { type, auth } = useAuthContext();

  useEffect(() => {
    if (moment().isAfter(CHAT_INBOX_EXPIRY_DATE))
      cleanupChatInboxPromoStorage();
  }, []);

  // Only partners whose Chat actually moved into the Inbox — same gate the nav
  // uses to swap the Chat menu item for Inbox.
  const isChatInboxAudience =
    type.isNativeChat && (type.isBoomScreen || type.isBoomCRM);

  const promoContext = {
    isBoomScreen: type.isBoomScreen,
    isChatInboxAudience,
    partnerCreatedAt: auth?.created_at,
    sessionPromoData: null,
    modalSeen: Boolean(modalSeen),
    dismissed: Boolean(dismissed)
  };

  const showChatInboxModal = shouldApplyPromo(
    PromoFeatureName.ChatMovedToInbox,
    PromoType.Modal,
    promoContext
  );

  const showChatInboxBanner = shouldApplyPromo(
    PromoFeatureName.ChatMovedToInbox,
    PromoType.Banner,
    promoContext
  );

  return {
    showChatInboxModal,
    showChatInboxBanner
  };
};
